import React from 'react';

import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';

import { type BadgeProps, type BadgeTone } from './types';

type ToneStyle = { backgroundColor: string; borderColor: string; color: string };

const TONE_STYLES: Record<BadgeTone, ToneStyle> = {
  neutral: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: 'rgba(0, 0, 0, 0.1)',
    color: 'rgba(0, 0, 0, 0.9)',
  },
  error: {
    backgroundColor: 'rgba(184, 0, 34, 0.05)',
    borderColor: 'rgba(184, 0, 34, 0.2)',
    color: '#B80022',
  },
  warning: {
    backgroundColor: 'rgba(130, 89, 0, 0.05)',
    borderColor: 'rgba(130, 89, 0, 0.2)',
    color: '#825900',
  },
  success: {
    backgroundColor: 'rgba(0, 110, 62, 0.05)',
    borderColor: 'rgba(0, 110, 62, 0.2)',
    color: '#006E3E',
  },
  information: {
    backgroundColor: 'rgba(140, 11, 214, 0.05)',
    borderColor: 'rgba(140, 11, 214, 0.2)',
    color: '#8C0BD6',
  },
  brand: {
    backgroundColor: 'rgba(9, 95, 176, 0.05)',
    borderColor: 'rgba(9, 95, 176, 0.2)',
    color: '#095FB0',
  },
};

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: BadgeTone }>(({ theme, tone, size }) => {
  const isSmall = size === 'small';
  const typography = isSmall ? theme.typography.caption : theme.typography.body1;

  return {
    fontSize: typography.fontSize,
    lineHeight: typography.lineHeight,
    fontWeight: 400,
    padding: theme.spacing(0, 1),
    borderRadius: isSmall ? 4 : 8,
    borderWidth: '1px',
    borderStyle: 'solid',
    '& .MuiChip-label': {
      padding: theme.spacing(0, 0.5),
    },
    '& .MuiChip-icon': {
      paddingRight: theme.spacing(0.5),
      color: tone === 'neutral' ? 'rgba(0, 0, 0, 0.9)' : 'inherit',
      fontSize: 'inherit',
    },
    ...TONE_STYLES[tone],
  };
});

const Badge = React.memo(
  ({ label, tone = 'neutral', size = 'medium', icon, className, fdKey }: BadgeProps) => {
    return (
      <StyledChip
        className={className}
        data-fd={fdKey}
        icon={icon}
        label={label}
        size={size}
        tone={tone}
      />
    );
  }
);

Badge.displayName = 'Badge';

export default Badge;
